import React, { useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Lock, Pause, Rocket } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBrand } from '@/hooks/useBrand'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import PausePushDialog from './PausePushDialog'
import GoLiveConfirmDialog from './GoLiveConfirmDialog'
import { getRelativeTime, isPushPaused } from './DataFreshnessIndicator'
import type { SyncStatus } from '@/types/shared'

interface PushStatusBadgeProps {
  // `basePath` is the backend-served canonical path; the go-live PATCH is that plus '/go-live'.
  property: { basePath: string; name: string }
  syncStatus?: SyncStatus | null
}

interface PushStatusSharedProps {
  dryRun?: boolean
  liveAllowed?: boolean
  canGoLive?: boolean
  [key: string]: unknown
}

/**
 * The persistent live/paused indicator for the property header.
 *
 * Deliberately minimal: once a property is live there is nothing to DO, so the header
 * carries a quiet status dot rather than a standing call-to-action. The loud, full-width
 * banner (GoLiveControl on the dashboard) belongs to the PAUSED state only — a property
 * that is not pushing rates is an unfinished setup and should nag; a property that is
 * pushing rates is just working.
 *
 * Status is shown to EVERYONE in the account (knowing whether rates are reaching the PMS
 * is not privileged). Both actions are owner/admin only (canGoLive), mirroring the
 * CanCanCan deny seam. Pausing is password-confirmed, see PausePushDialog; going live is
 * not — it is the intended direction and its effect is loud.
 *
 * This badge is the PERMANENT home of both actions. The dashboard banner (GoLiveControl) is
 * a transient conversion surface that happens to offer go-live too — the two are peers that
 * open the same GoLiveConfirmDialog, and neither redirects to the other.
 */
export default function PushStatusBadge({ property, syncStatus }: PushStatusBadgeProps) {
  const { dryRun, liveAllowed, canGoLive } = usePage<PushStatusSharedProps>().props
  const brand = useBrand()
  const [open, setOpen] = useState(false)
  const [confirmPause, setConfirmPause] = useState(false)
  const [confirmGoLive, setConfirmGoLive] = useState(false)

  // Pre-backfill / non-property pages share no dryRun — render nothing rather than guess.
  if (dryRun === undefined) return null

  const isLive = dryRun === false
  const isOperator = canGoLive === true
  const canPause = isLive && isOperator
  const canTakeLive = !isLive && isOperator
  // Display hint only — ToggleGoLiveMutation is the server-side entitlement gate.
  const billingBlocked = liveAllowed === false
  // A paused property stamps a fictional push (logged, never sent), so the timestamp is a
  // CHECK, not a send. Same honesty rule the pipeline strip follows.
  const pushIsFictional = isPushPaused(dryRun, syncStatus?.lastPushDryRun)
  const lastPushAt = syncStatus?.lastPush ?? null
  const pushCount = syncStatus?.lastPushCount

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          aria-label={isLive ? 'Live — pushing rates to your PMS' : 'Paused — rates not being pushed'}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
            'cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            isLive
              ? 'bg-primary/10 text-primary hover:bg-primary/15'
              : 'bg-amber-100 text-amber-700 hover:bg-amber-200/70'
          )}
        >
          <span
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full', isLive ? 'bg-primary' : 'bg-amber-500')}
            aria-hidden="true"
          />
          <span className="hidden sm:inline">{isLive ? 'Live' : 'Paused'}</span>
        </PopoverTrigger>

        <PopoverContent className="w-[288px]">
          <div className="px-4 py-3">
            <div className="flex items-center gap-2">
              <span
                className={cn('h-1.5 w-1.5 shrink-0 rounded-full', isLive ? 'bg-primary' : 'bg-amber-500')}
                aria-hidden="true"
              />
              <span className="text-sm font-semibold text-foreground">{isLive ? 'Live' : 'Paused'}</span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {isLive
                ? `${brand} is pushing this property’s rates to your PMS on every computation.`
                : `Computed rates stay in ${brand}. Nothing is sent to your PMS while paused.`}
            </p>
          </div>

          <div className="flex items-baseline justify-between border-t border-border/40 px-4 py-2.5">
            <span className="text-xs text-muted-foreground">
              {pushIsFictional ? 'Last check' : 'Last push'}
            </span>
            <span className="text-xs font-medium tabular-nums text-foreground">
              {getRelativeTime(lastPushAt)}
              {!pushIsFictional && typeof pushCount === 'number' && pushCount > 0 && (
                <span className="ml-1 font-normal text-muted-foreground">
                  · {pushCount} rate{pushCount === 1 ? '' : 's'}
                </span>
              )}
            </span>
          </div>

          {canPause && (
            <div className="border-t border-border/40 p-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  setConfirmPause(true)
                }}
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
              >
                <Pause className="h-3.5 w-3.5" />
                Pause rate pushing
              </button>
            </div>
          )}

          {canTakeLive && (
            <div className="border-t border-border/40 p-2">
              <button
                type="button"
                disabled={billingBlocked}
                onClick={() => {
                  setOpen(false)
                  setConfirmGoLive(true)
                }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:bg-transparent enabled:cursor-pointer"
              >
                {billingBlocked ? <Lock className="h-3.5 w-3.5" /> : <Rocket className="h-3.5 w-3.5" />}
                Go live
              </button>
              {billingBlocked && (
                <p className="px-2 pb-1 pt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                  Billing is not active for this account.
                </p>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>

      <PausePushDialog
        propertyBasePath={property.basePath}
        propertyName={property.name}
        open={confirmPause}
        onOpenChange={setConfirmPause}
      />
      <GoLiveConfirmDialog
        propertyBasePath={property.basePath}
        open={confirmGoLive}
        onOpenChange={setConfirmGoLive}
      />
    </>
  )
}
